import styled from '@emotion/styled';

import { Icon, Stack, Switch, Text, tokens, Tooltip } from '@hotjar/hotsauce';
import type { BillingPeriod } from '@hotjar/subscription-management';
import { useTranslation } from 'react-i18next';

const S = {
  Stack: styled(Stack)`
    align-items: center;
    display: inline-flex;
  `,
  Switch: styled(Switch)`
    margin: 0;
  `,
  Label: styled(Text)`
    ${tokens.text('body2')};

    color: ${tokens.color.ui_active};
  `,
  Badge: styled.div`
    ${tokens.text('subtitle2')};

    align-items: center;
    background: ${tokens.color.bg_success};
    border-radius: ${tokens.radius.m};
    border: 1px solid ${tokens.color.ui_border_success};
    color: ${tokens.color.text_success};
    display: inline-flex;
    justify-content: center;
    max-height: ${tokens.spacer.x3};
    padding: 2px ${tokens.spacer.x0};
    text-align: center;
  `,
  IconWrapper: styled.span`
    display: inline-flex;
  `,
};

const offValue = 'MONTHLY';
const onValue = 'ANNUALLY';

interface Props {
  value: BillingPeriod;
  isLocked?: boolean;
  onChange: (newValue: BillingPeriod) => unknown;
}

/**
 * Renders a billing period switch that can be toggled using string values from the BillingPeriod type.
 * This component is meant to be used in upsell modals, to allow the user to choose between monthly and annual billing.
 */
export const BillingPeriodSwitch = ({ value, isLocked = false, onChange }: Props): JSX.Element => {
  const { t } = useTranslation('upsell');

  return (
    <S.Stack data-testid="billing-period-switch" direction="row" spacing="x1">
      <Tooltip
        text={t('billing-period-switch.discount-tooltip')}
        isDisabled={isLocked}
        placement="bottom-start"
      >
        <div>
          <S.Switch
            labelContent={<S.Label>{t('billing-period-switch.pay-yearly')}</S.Label>}
            isDisabled={isLocked}
            isChecked={value === onValue}
            onChange={() => onChange(value === onValue ? offValue : onValue)}
          />
        </div>
      </Tooltip>

      <S.Badge>{t('billing-period-switch.discount-badge')}</S.Badge>

      {value === onValue && isLocked ? (
        <Tooltip text={t('billing-period-switch.locked-tooltip')} placement="bottom-start">
          <S.IconWrapper data-testid="billing-period-switch-locked-icon">
            <Icon icon="lock" color="text_disabled" />
          </S.IconWrapper>
        </Tooltip>
      ) : null}
    </S.Stack>
  );
};
